/**
 * Client wrapper around the Supabase Edge Function `support-message`.
 *
 * Public endpoint — no auth required, so anonymous prospects can reach us.
 * The function uses Mailgun to email ADMIN_EMAIL.
 */

import { supabase, isSupabaseConfigured } from "./supabase";

export interface SupportMessage {
  name?: string;
  email: string;
  subject: string;
  message: string;
  /** Honeypot — must stay empty. Filled only by bots. */
  _hp?: string;
  /** Cloudflare Turnstile token from the widget. */
  turnstile_token?: string;
}

export class SupportError extends Error {
  code: string;
  constructor(code: string, message: string) {
    super(message);
    this.code = code;
    this.name = "SupportError";
  }
}

export async function sendSupportMessage(input: SupportMessage): Promise<void> {
  if (!isSupabaseConfigured) {
    // Mock mode (local dev): pretend we sent it.
    await new Promise((r) => setTimeout(r, 600));
    // eslint-disable-next-line no-console
    console.info("[support] mock send", input);
    return;
  }

  const url = import.meta.env.VITE_SUPABASE_URL as string;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

  // Optional: piggyback the user's JWT if they're signed in, for cross-referencing
  // future support tickets to accounts. Function doesn't require it.
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData.session?.access_token;

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    apikey: anonKey,
  };
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  const response = await fetch(`${url}/functions/v1/support-message`, {
    method: "POST",
    headers,
    body: JSON.stringify(input),
  });

  const body = await response.json().catch(() => null);
  if (!response.ok || !body?.ok) {
    const code = body?.error?.code ?? "request_failed";
    const message = body?.error?.message ?? `Request failed with status ${response.status}`;
    throw new SupportError(code, message);
  }
}
